import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { validateFullUrlPath } from '@deltek/specification-client/core/Utilities/urlUtils'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import DeleteIcon from '@material-ui/icons/Delete'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import clsx from 'clsx'
import React from 'react'
import { Draggable } from 'react-beautiful-dnd'
import { NormalizedListingDetailLinkInput } from './EditLinks'

const useStyles = makeStyles((theme: Theme) => ({
    iconButton: {
        height: 36,
        width: 34,
        padding: 0,
        margin: 0,
        '& .MuiIconButton-label': {
            height: '100%',
        },
        '& .MuiIcon-root': {
            height: 'unset',
            width: 'unset',
        },
    },
    withBorder: {
        borderBottom: '1px solid #04325B',
    },
    withoutBorder: {
        borderBottom: '1px solid #FFFFFF',
    },
    withPadding: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    field: {
        marginTop: 2,
        marginBottom: 2,
    },
    dragIcon: { fill: '#C9C9C9' },
    input: {
        paddingTop: theme.spacing(1.75),
        paddingBottom: theme.spacing(1.75),
    },
    errorWrapper: {
        marginTop: -22,
    },
}))

interface EditLinkRowProps {
    detail: NormalizedListingDetailLinkInput
    onlyOne?: boolean
    last?: boolean
    handleUpdateLink: (detail: NormalizedListingDetailLinkInput) => void
    handleDeleteLink: (id: number) => void
    index: number
}

export const EditLinkRow: React.FC<EditLinkRowProps> = ({
    last,
    onlyOne,
    detail,
    handleUpdateLink,
    handleDeleteLink,
    index,
}) => {
    const classes = useStyles()

    const inputRef = React.useRef<HTMLInputElement>(null)

    const debouncedTypeInput = React.useCallback(
        (value: string) => {
            handleUpdateLink({
                ...detail,
                title: {
                    ...detail.title,
                    valid: onlyOne && !value ? true : !!value.trim(),
                    value,
                },
                url: {
                    ...detail.url,
                    valid:
                        onlyOne && !value
                            ? true
                            : validateFullUrlPath(detail.url.value ?? ''),
                },
            })
        },
        [handleUpdateLink, detail, onlyOne]
    )

    const debouncedUrlInput = React.useCallback(
        (value: string) => {
            handleUpdateLink({
                ...detail,
                title: {
                    ...detail.title,
                    valid:
                        onlyOne && !value ? true : !!detail.title.value?.trim(),
                },
                url: {
                    ...detail.url,
                    valid:
                        onlyOne && !value ? true : validateFullUrlPath(value),
                    value,
                },
            })
        },
        [handleUpdateLink, detail, onlyOne]
    )

    const handleTypeChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        debouncedTypeInput(event.target.value)
    }

    const handleUrlChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        debouncedUrlInput(event.target.value)
    }

    const handleDeleteClick = () => {
        handleDeleteLink(detail.id)
        if (onlyOne && inputRef.current) {
            inputRef?.current?.focus()
        }
    }

    const handleOnBlur = () => {
        if (onlyOne && !detail.title.value && !detail.url.value) {
            handleUpdateLink({
                ...detail,
                title: { ...detail.title, valid: true },
                url: {
                    ...detail.url,
                    valid: true,
                },
            })
        } else {
            handleUpdateLink({
                ...detail,
                title: { ...detail.title, valid: !!detail.title.value?.trim() },
                url: {
                    ...detail.url,
                    valid: validateFullUrlPath(detail.url.value ?? ''),
                },
            })
        }
    }

    return (
        <Draggable
            draggableId={detail.id.toString()}
            index={index}
            isDragDisabled={onlyOne}
        >
            {(provided) => (
                <GridContainer
                    className="dvLink"
                    direction="row"
                    justify="center"
                    alignItems="stretch"
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                >
                    <GridContainer
                        item
                        xs={1}
                        alignItems="center"
                        className={clsx(
                            classes.withPadding,
                            (!detail.title.valid || !detail.url.valid) &&
                                classes.errorWrapper
                        )}
                    >
                        {!onlyOne && (
                            <div {...provided.dragHandleProps}>
                                <DragIndicatorIcon
                                    className={clsx(
                                        'svgDragIcon',
                                        classes.dragIcon
                                    )}
                                    fontSize="large"
                                />
                            </div>
                        )}
                    </GridContainer>
                    <GridContainer
                        item
                        xs={10}
                        alignItems="center"
                        className={clsx(
                            classes.withPadding,
                            last ? classes.withoutBorder : classes.withBorder
                        )}
                    >
                        <TextField
                            className={clsx('dvTitle', classes.field)}
                            placeholder="Add title here"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            value={detail.title.value}
                            onChange={handleTypeChange}
                            InputProps={{
                                classes: {
                                    input: classes.input,
                                },
                            }}
                            error={!detail.title.valid}
                            helperText={
                                !detail.title.valid ? 'Must have a value.' : ''
                            }
                            required={detail.title.required}
                            autoComplete="off"
                            onBlur={handleOnBlur}
                            inputRef={inputRef}
                            inputProps={{
                                maxLength: 50,
                            }}
                            autoFocus={detail.new ?? false}
                        />
                        <TextField
                            className={clsx('dvUrl', classes.field)}
                            placeholder="Add link here"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            value={detail.url.value}
                            onChange={handleUrlChange}
                            InputProps={{
                                classes: {
                                    input: classes.input,
                                },
                            }}
                            error={!detail.url.valid}
                            helperText={
                                !detail.url.valid
                                    ? 'Invalid Link. Please enter a valid URL.'
                                    : ''
                            }
                            required={detail.url.required}
                            autoComplete="off"
                            onBlur={handleOnBlur}
                        />
                    </GridContainer>
                    <GridContainer
                        item
                        xs={1}
                        alignItems="center"
                        className={clsx(
                            classes.withPadding,
                            last ? classes.withoutBorder : classes.withBorder,
                            (!detail.title.valid || !detail.url.valid) &&
                                classes.errorWrapper
                        )}
                    >
                        <IconButton
                            data-testid="btnDeleteLink"
                            className={classes.iconButton}
                            onClick={handleDeleteClick}
                        >
                            <DeleteIcon color="primary" />
                        </IconButton>
                    </GridContainer>
                </GridContainer>
            )}
        </Draggable>
    )
}
